import React, { useEffect, useState, useCallback } from 'react';
import { getCreatedQrList, downloadQrList } from '../services/qrService';
import ActionButton from '../components/common/ActionButton';
import CreateQrModal from '../components/QrModals/CreateQrModal';
import { formatDate, getDayName, isToday } from '../helpers/stringHelpers';

const Dashboard = () => {
  const [qrList, setQrList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    id: '',
    date: ''
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Memoize the callbacks
  const handleModalClose = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCreateSuccess = useCallback((result) => {
    setQrList(prevList => {
      // Add the new QR to the list without causing unnecessary re-renders
      const newList = [...prevList];
      newList.unshift(result);
      return newList;
    });
  }, []);

  useEffect(() => {
    fetchQrList();
  }, [filters]);

  const fetchQrList = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getCreatedQrList(filters.id, filters.date);
      setQrList(data);
    } catch (error) {
      console.error('Failed to fetch QR list:', error);
      setError('QR listesi yüklenirken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const groupQrsByDate = () => {
    const groups = {};
    qrList.forEach(qr => {
      const date = formatDate(qr.secilentarihTarihi);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(qr);
    });

    // Sort dates in descending order (newest to oldest)
    return Object.entries(groups)
      .sort(([_, qrsA], [__, qrsB]) => {
        const dateA = new Date(qrsA[0].secilentarihTarihi);
        const dateB = new Date(qrsB[0].secilentarihTarihi);
        return dateB - dateA;
      });
  };

  const handleDownload = async (id) => {
    try {
      await downloadQrList(id);
      // Refresh the list after download
      fetchQrList();
    } catch (error) {
      console.error('Failed to download QR:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <h1 className='page-title'>Önceki Oluşturulan QR Kodlar</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="people-container">
        <h1 className="page-title">Önceki Oluşturulan QR Kodlar</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className='page-title'>Önceki Oluşturulan QR Kodlar</h1>
      </div>
      <ActionButton 
          title="Yeni QR Kod Oluştur"
          onClick={() => setCreateModalOpen(true)}
      />


      {qrList.length === 0 ? (
        <p className="no-data">Henüz QR kod oluşturulmamış.</p>
      ) : (
        <div className="qr-groups">
          {groupQrsByDate().map(([date, qrs]) => (
            <div key={date} className="qr-date-group">
              <h2 className="date-header">
                <span>{date},</span>
                <span className="day-name">
                  {getDayName(qrs[0].olusturulmaTarihi)}
                  {isToday(qrs[0].olusturulmaTarihi) && (
                    <span>
                      {' '}(Bugün)
                    </span>
                  )}
                </span>
              </h2>
              <div className="qr-grid">
                {qrs.map((qr) => (
                  <div key={qr.id} className="qr-item">
                    <div className="qr-info">
                      <div className="qr-header">
                        <p>{qr.count}</p>
                        <p>QR Kod</p>
                      </div>
                      {(() => {
                        const creationTime = new Date(qr.olusturulmaTarihi);
                        const oneHourLater = new Date(creationTime.getTime() + 60 * 60 * 1000);
                        const now = new Date();
                        return now <= oneHourLater && (
                          <button 
                            onClick={() => handleDownload(qr.id)}
                            className="download-button"
                          >
                            İndir
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateQrModal 
        isOpen={createModalOpen}
        onClose={handleModalClose}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Dashboard; 